import { Component, OnInit, OnDestroy, Injector, ViewEncapsulation, ViewChild, HostListener, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AppComponentBase } from '@shared/common/app-component-base';
import { LocalizePipe } from '@shared/common/pipes/localize.pipe';
import { ProfileServiceProxy } from '@shared/service-proxies/service-proxies';
import { finalize } from 'rxjs/operators';
import { RcopiaPopupComponent } from '../../rcopia-popup/rcopia-popup.component';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';

export interface RcopiaNotification {
    typeOfNotification: string;
    notificationNumber: number;
    [key: string]: any;
}

@Component({
    selector: 'rcopia-notification-dropdown',
    templateUrl: './rcopia-notification-dropdown.component.html',
    styleUrls: ['./rcopia-notification-dropdown.component.less'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        CommonModule,
        LocalizePipe,
        RcopiaPopupComponent,
        BsDropdownModule
    ]
})
export class RcopiaNotificationDropdownComponent extends AppComponentBase implements OnInit, OnDestroy {
    @ViewChild('rcopiaPopUpModal') rcopiaPopUpModal: RcopiaPopupComponent;

    recopiaNotifications: RcopiaNotification[] = [];
    loading = false;
    isDropdownOpen = false;
    private refreshTimer: any;

    constructor(
        injector: Injector,
        private _profileServiceProxy: ProfileServiceProxy,
        private elementRef: ElementRef
    ) {
        super(injector);
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        const clickedInside = this.elementRef.nativeElement.contains(event.target);
        if (!clickedInside && this.isDropdownOpen) {
            this.closeDropdown();
        }
    }

    @HostListener('click', ['$event'])
    onComponentClick(event: MouseEvent): void {
        event.stopPropagation();
    }

    ngOnInit(): void {
        this.loadNotifications();
        this.registerToEvents();
        // Auto-refresh every 60 seconds to pick up Hangfire job updates
        this.refreshTimer = setInterval(() => {
            this.loadNotifications();
        }, 60000);
    }

    ngOnDestroy(): void {
        if (this.refreshTimer) {
            clearInterval(this.refreshTimer);
        }
    }

    registerToEvents(): void {
        abp.event.on('app.rcopia.notificationsChanged', () => {
            this.loadNotifications();
        });
    }

    loadNotifications(): void {
        this.loading = true;
        this._profileServiceProxy.getAllRcopiaNotificaitons()
            .pipe(finalize(() => this.loading = false))
            .subscribe(result => {
                this.recopiaNotifications = result || [];
            });
    }

    toggleDropdown(event?: Event): void {
        if (event) {
            event.stopPropagation();
        }
        this.isDropdownOpen = !this.isDropdownOpen;
    }

    closeDropdown(): void {
        this.isDropdownOpen = false;
    }

    onNotificationClick(notification: RcopiaNotification): void {
        this.closeDropdown();
        setTimeout(() => {
            const screen = this.getNotificationScreen(notification.typeOfNotification);
            this.redirectToRcopia(screen);
        }, 100);
    }

    redirectToRcopia(notificationScreen: string): void {
        if (this.rcopiaPopUpModal) {
            this.rcopiaPopUpModal.showForNotification(notificationScreen);
        }
    }

    getNotificationScreen(type: string): string {
        const screens = {
            'rx_pending': 'report',
            'refill': 'message',
            'rx_need_signing': 'report',
            'rxchange': 'message'
        };
        return screens[type] || 'report';
    }

    getNotificationLabel(type: string): string {
        const labels = {
            'rx_pending': 'Rx Pending',
            'refill': 'Refill',
            'rx_need_signing': 'Rx Need Signing',
            'rxchange': 'Rx Change'
        };
        return labels[type] || type;
    }

    get totalNotifications(): number {
        return this.recopiaNotifications.reduce((sum, n) => sum + (n.notificationNumber || 0), 0);
    }

    get hasNotifications(): boolean {
        return this.recopiaNotifications.length > 0;
    }
}